import React from "react";
import { cn } from "../../lib/cn";
import { Eyebrow } from "./Eyebrow";
import { ThemeToggle } from "./ThemeToggle";

/**
 * Split-screen auth shell: dark navy brand panel + light form panel.
 * Used by Login / Signup / Forgot / Reset / OTP for a consistent premium entry.
 *   <AuthLayout eyebrow="KBC · Member access" title={<>Welcome <em className="kbc-serif">back</em></>}
 *               subtitle="...">{form}</AuthLayout>
 */
export function AuthLayout({ eyebrow, title, subtitle, brandPoints, children, footer }) {
  const points = brandPoints || [ "Verified community business directory", "Connect, refer and grow together", "One trusted network for every member",
  ];
  return (
    <div className="min-h-screen w-full bg-bg lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden border-r border-white/10 bg-navy px-9 py-10 text-[#C7D3DF] lg:flex lg:flex-col lg:justify-between">
        <BrandMark className="relative z-10 text-white" />
        <div className="relative z-10 max-w-md">
          <Eyebrow className="!text-[#9FB4C6]">Kongu Business Community</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight text-[#F2F6F9]">
            The community that <em className="kbc-serif" style={{ color: "#5be2c9" }}>does business</em> together.
          </h2>
          <ul className="mt-5 space-y-2">
            {points.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm text-[#AFC2D2]">
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.2 2.3L9.5 3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 font-mono text-[0.68rem] uppercase tracking-kicker text-[#7E92A6]">
          Salem · Tamil Nadu · onekbc.org
        </p>
      </aside>

      {/* Form panel */}
      {/* justify-start below lg: on a tall phone the brand panel is hidden, so
          vertically centring the form left ~400px of dead space above it.
          Centring is only right once the split layout is in play. */}
      <main className="relative flex min-h-screen flex-col justify-start px-5 py-6 sm:px-10 lg:justify-center lg:px-16 lg:py-10">
        <div className="absolute right-5 top-5 sm:right-8 sm:top-8">
          <ThemeToggle />
        </div>
        <div className="mx-auto w-full max-w-md">
          <div className="mb-6 lg:hidden">
            <BrandMark />
          </div>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {title && (
            <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">{title}</h1>
          )}
          {subtitle && <p className="mt-2 text-sm leading-relaxed text-text">{subtitle}</p>}
          <div className="mt-6">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-muted">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

export function BrandMark({ className }) {
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <span className="relative inline-block h-6 w-[3px] rounded-full bg-primary">
        <span className="absolute -top-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-accent" />
      </span>
      <span className="font-display text-base font-semibold uppercase tracking-[0.32em]">KBC</span>
    </span>
  );
}

export default AuthLayout;
